/**
 * Read a file's true creation date from its embedded metadata when possible:
 *   - MP4 / MOV / M4V: ISO BMFF `mvhd` creation_time inside the `moov` atom
 *   - JPEG:            EXIF DateTimeOriginal (APP1 marker)
 *   - HEIC / HEIF:     EXIF DateTimeOriginal inside the ISO BMFF `meta` box
 *
 * Then tries a date embedded in the filename (VID_20260925_…, PXL_…, DJI_…,
 * "Screen Recording 2026-09-25 …"), and only then falls back to
 * `file.lastModified` — which is often the day the file was copied, not filmed.
 *
 * Why: video and image files almost always stamp the capture moment in
 * metadata. The OS file mtime can be wrong after a copy/export, but EXIF /
 * mvhd survive most workflows and reflect when the camera actually rolled.
 */

export type DateSource = "moov" | "exif" | "filename" | "lastModified";

export interface FileCreationDate {
  date: Date;
  source: DateSource;
  /** True when we couldn't read embedded metadata and fell back to lastModified. */
  isFallback: boolean;
}

/** Cap how many top-level boxes we'll step over. Each step reads only a
 *  16-byte header, so seeking past a multi-GB `mdat` is cheap — there is
 *  deliberately NO byte-offset cap: most dedicated cameras write `moov` at
 *  the END of the file, and a byte cap made every clip over the cap silently
 *  fall back to lastModified (i.e. the copy/upload day). */
const MAX_BOX_WALK_COUNT = 256;
/** mvhd is always near the start of moov; don't read a huge moov in full. */
const MOOV_READ_BYTES = 1024 * 1024;

/** Seconds between 1904-01-01 (QuickTime/ISO BMFF epoch) and 1970-01-01 (Unix). */
const EPOCH_OFFSET_1904_TO_1970 = 2_082_844_800;

export async function getFileCreationDate(file: File): Promise<FileCreationDate> {
  const name = file.name.toLowerCase();
  const fromName = parseDateFromFilename(file.name);
  try {
    let embedded: { date: Date; source: DateSource } | null = null;
    if (name.endsWith(".mp4") || name.endsWith(".mov") || name.endsWith(".m4v")) {
      const d = await readMp4CreationTime(file);
      if (d) embedded = { date: d, source: "moov" };
    } else if (name.endsWith(".jpg") || name.endsWith(".jpeg")) {
      const d = await readJpegExifDate(file);
      if (d) embedded = { date: d, source: "exif" };
    } else if (name.endsWith(".heic") || name.endsWith(".heif")) {
      const d = await readHeicExifDate(file);
      if (d) embedded = { date: d, source: "exif" };
    }
    if (embedded) {
      // A filename capture date that's an EARLIER day wins: some apps
      // (notably the DJI app — "DJI_20260930_111742_920_video.MP4") re-stamp
      // the embedded date with the day the clip was saved/downloaded, while
      // the filename keeps the day it was filmed. Nothing is filmed after
      // it's saved, so the earlier day is the real one.
      if (fromName && startOfDay(fromName) < startOfDay(embedded.date)) {
        return { date: fromName, source: "filename", isFallback: false };
      }
      return { ...embedded, isFallback: false };
    }
  } catch {
    /* Any parse failure → fall through to filename / lastModified */
  }
  if (fromName) return { date: fromName, source: "filename", isFallback: false };
  return {
    date: new Date(file.lastModified),
    source: "lastModified",
    isFallback: true,
  };
}

/** Format a Date as MM.DD.YY (the folder naming convention used everywhere
 *  else in the app). Kept here so callers don't need to import date-fns. */
export function formatDateFolder(date: Date): string {
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  const yy = String(date.getFullYear()).slice(-2);
  return `${mm}.${dd}.${yy}`;
}

function startOfDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/** Pull a YYYYMMDD / YYYY-MM-DD / YYYY_MM_DD date out of a filename, e.g.
 *  "VID_20260925_101500.mp4", "PXL_20260925_…", "DJI_20260925…",
 *  "Screen Recording 2026-09-25 at 10.15.mov". Local-time midnight. */
export function parseDateFromFilename(name: string): Date | null {
  const m = name.match(/(?:^|[^0-9])(20\d{2})[-_.]?(\d{2})[-_.]?(\d{2})(?![0-9])|(?:^|[^0-9])(20\d{2})(\d{2})(\d{2})[_T-]?\d{4,6}/);
  if (!m) return null;
  const [y, mo, d] = m[1] ? [m[1], m[2], m[3]] : [m[4], m[5], m[6]];
  const date = new Date(Number(y), Number(mo) - 1, Number(d));
  if (
    date.getFullYear() !== Number(y) ||
    date.getMonth() !== Number(mo) - 1 ||
    date.getDate() !== Number(d)
  ) return null; // rejects 2026-13-45 etc.
  if (date.getTime() > Date.now() + 24 * 3600 * 1000) return null;
  return date;
}

// ─── MP4 / MOV ───────────────────────────────────────────────────────────────

async function readSlice(file: File, start: number, end: number): Promise<DataView> {
  const buf = await file.slice(start, end).arrayBuffer();
  return new DataView(buf);
}

/** Walk top-level ISO BMFF boxes until we find `moov`, then return its bytes. */
async function findMoov(file: File): Promise<DataView | null> {
  let offset = 0;
  let count = 0;
  while (offset < file.size && count < MAX_BOX_WALK_COUNT) {
    const header = await readSlice(file, offset, Math.min(offset + 16, file.size));
    if (header.byteLength < 8) return null;
    let size = header.getUint32(0);
    const type = String.fromCharCode(header.getUint8(4), header.getUint8(5), header.getUint8(6), header.getUint8(7));
    let headerLen = 8;
    if (size === 1) {
      if (header.byteLength < 16) return null;
      const hi = header.getUint32(8);
      const lo = header.getUint32(12);
      size = hi * 0x1_0000_0000 + lo;
      headerLen = 16;
    } else if (size === 0) {
      // Box extends to EOF
      size = file.size - offset;
    }
    if (type === "moov") {
      const end = Math.min(offset + size, offset + headerLen + MOOV_READ_BYTES, file.size);
      return readSlice(file, offset + headerLen, end);
    }
    if (size <= 0) return null;
    offset += size;
    count++;
  }
  return null;
}

/** Read mvhd creation_time from inside a moov DataView. mvhd is a top-level
 *  child of moov; we search for its 4-byte type marker rather than parsing
 *  every intermediate box, since mvhd is always early in moov. */
function parseMvhdCreationTime(moov: DataView): Date | null {
  const bytes = new Uint8Array(moov.buffer, moov.byteOffset, moov.byteLength);
  // Search for "mvhd" type marker (preceded by 4-byte size). Limit search.
  const limit = Math.min(bytes.length - 4, 4096);
  for (let i = 0; i < limit; i++) {
    if (
      bytes[i] === 0x6d /* m */ &&
      bytes[i + 1] === 0x76 /* v */ &&
      bytes[i + 2] === 0x68 /* h */ &&
      bytes[i + 3] === 0x64 /* d */
    ) {
      // i points at "mvhd"; payload starts at i+4. First byte is version.
      const payloadStart = i + 4;
      if (payloadStart + 20 > bytes.length) return null;
      const version = moov.getUint8(payloadStart);
      // version 0: creation_time at +4 (32-bit), version 1: at +4 (64-bit)
      const ctOffset = payloadStart + 4;
      let secondsSince1904: number;
      if (version === 1) {
        if (ctOffset + 8 > bytes.length) return null;
        const hi = moov.getUint32(ctOffset);
        const lo = moov.getUint32(ctOffset + 4);
        secondsSince1904 = hi * 0x1_0000_0000 + lo;
      } else {
        if (ctOffset + 4 > bytes.length) return null;
        secondsSince1904 = moov.getUint32(ctOffset);
      }
      if (secondsSince1904 === 0) return null;
      const unixSeconds = secondsSince1904 - EPOCH_OFFSET_1904_TO_1970;
      const date = new Date(unixSeconds * 1000);
      // Sanity check: ignore obviously-bogus dates (some cameras write 0 or 1970)
      const year = date.getUTCFullYear();
      if (year < 2000 || year > 2100) return null;
      return date;
    }
  }
  return null;
}

async function readMp4CreationTime(file: File): Promise<Date | null> {
  const moov = await findMoov(file);
  if (!moov) return null;
  return parseMvhdCreationTime(moov);
}

// ─── JPEG EXIF ───────────────────────────────────────────────────────────────

/** Parse "YYYY:MM:DD HH:MM:SS" (EXIF DateTimeOriginal format) as a local-time
 *  Date. EXIF doesn't include timezone, so we interpret as local — matching
 *  how the user thinks about "session date". */
function parseExifDateTime(s: string): Date | null {
  const m = s.match(/^(\d{4}):(\d{2}):(\d{2}) (\d{2}):(\d{2}):(\d{2})/);
  if (!m) return null;
  const [, y, mo, d, h, mi, se] = m;
  const date = new Date(
    Number(y),
    Number(mo) - 1,
    Number(d),
    Number(h),
    Number(mi),
    Number(se),
  );
  if (isNaN(date.getTime())) return null;
  return date;
}

/** Walk EXIF TIFF IFD entries looking for DateTimeOriginal (0x9003) or, as a
 *  fallback, DateTime (0x0132). Returns the first hit. */
function readExifDateFromTiff(tiff: DataView): Date | null {
  if (tiff.byteLength < 8) return null;
  const byteOrder = tiff.getUint16(0);
  const little = byteOrder === 0x4949; // "II"
  const big = byteOrder === 0x4d4d; // "MM"
  if (!little && !big) return null;
  const u16 = (off: number) => tiff.getUint16(off, little);
  const u32 = (off: number) => tiff.getUint32(off, little);
  if (u16(2) !== 0x002a) return null;
  const ifd0Offset = u32(4);
  if (ifd0Offset + 2 > tiff.byteLength) return null;

  const readDateAtIfd = (ifdOff: number): { date: Date | null; exifIfdOff: number | null } => {
    if (ifdOff + 2 > tiff.byteLength) return { date: null, exifIfdOff: null };
    const count = u16(ifdOff);
    let exifIfdOff: number | null = null;
    let dateTime: Date | null = null;
    let dateTimeOriginal: Date | null = null;
    for (let i = 0; i < count; i++) {
      const entry = ifdOff + 2 + i * 12;
      if (entry + 12 > tiff.byteLength) break;
      const tag = u16(entry);
      const valueOffset = u32(entry + 8);
      if (tag === 0x8769) {
        // ExifIFDPointer
        exifIfdOff = valueOffset;
      } else if (tag === 0x0132 || tag === 0x9003) {
        // ASCII string of length 20 ("YYYY:MM:DD HH:MM:SS\0")
        const strOff = valueOffset;
        if (strOff + 19 <= tiff.byteLength) {
          let s = "";
          for (let k = 0; k < 19; k++) s += String.fromCharCode(tiff.getUint8(strOff + k));
          const parsed = parseExifDateTime(s);
          if (parsed) {
            if (tag === 0x9003) dateTimeOriginal = parsed;
            else dateTime = parsed;
          }
        }
      }
    }
    return { date: dateTimeOriginal ?? dateTime, exifIfdOff };
  };

  const ifd0 = readDateAtIfd(ifd0Offset);
  if (ifd0.date) return ifd0.date;
  if (ifd0.exifIfdOff != null) {
    const subResult = readDateAtIfd(ifd0.exifIfdOff);
    if (subResult.date) return subResult.date;
  }
  return null;
}

async function readJpegExifDate(file: File): Promise<Date | null> {
  // EXIF lives in APP1 near the start. Reading 256 KB is plenty.
  const head = await readSlice(file, 0, Math.min(256 * 1024, file.size));
  if (head.byteLength < 4) return null;
  // SOI = FFD8
  if (head.getUint16(0) !== 0xffd8) return null;
  let offset = 2;
  while (offset < head.byteLength - 4) {
    const marker = head.getUint16(offset);
    const segLen = head.getUint16(offset + 2);
    if ((marker & 0xff00) !== 0xff00) return null;
    if (marker === 0xffe1) {
      // APP1 — check "Exif\0\0" header
      const hStart = offset + 4;
      if (hStart + 6 > head.byteLength) return null;
      const isExif =
        head.getUint8(hStart) === 0x45 && // E
        head.getUint8(hStart + 1) === 0x78 && // x
        head.getUint8(hStart + 2) === 0x69 && // i
        head.getUint8(hStart + 3) === 0x66; // f
      if (isExif) {
        const tiffStart = hStart + 6;
        const tiff = new DataView(head.buffer, head.byteOffset + tiffStart, head.byteLength - tiffStart);
        return readExifDateFromTiff(tiff);
      }
    }
    offset += 2 + segLen;
  }
  return null;
}

// ─── HEIC / HEIF ─────────────────────────────────────────────────────────────

/** HEIC stores EXIF inside an `iinf`/`iloc`-referenced item in the `meta` box.
 *  Fully parsing that chain is heavy; instead we do a bounded scan of the
 *  first ~1 MB for the EXIF TIFF header ("II*\0" or "MM\0*"). This catches
 *  iPhone HEICs reliably without pulling in a HEIF parser. */
async function readHeicExifDate(file: File): Promise<Date | null> {
  const scan = await readSlice(file, 0, Math.min(1024 * 1024, file.size));
  const bytes = new Uint8Array(scan.buffer, scan.byteOffset, scan.byteLength);
  for (let i = 0; i < bytes.length - 8; i++) {
    const isLittle = bytes[i] === 0x49 && bytes[i + 1] === 0x49 && bytes[i + 2] === 0x2a && bytes[i + 3] === 0x00;
    const isBig = bytes[i] === 0x4d && bytes[i + 1] === 0x4d && bytes[i + 2] === 0x00 && bytes[i + 3] === 0x2a;
    if (isLittle || isBig) {
      const tiff = new DataView(scan.buffer, scan.byteOffset + i, scan.byteLength - i);
      const d = readExifDateFromTiff(tiff);
      if (d) return d;
    }
  }
  return null;
}
